/**
 * Recursively flattens an object using dot notation for nested properties.
 *
 * @param obj - Object to flatten
 * @param prefix - Prefix for the current level (e.g., 'data')
 * @param result - Target object to store flattened properties
 */
const flattenObject = (obj: Record<string, any>, prefix: string, result: Record<string, any>): void => {
    for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== undefined && obj[key] !== null) {
            const flatKey = `${prefix}.${key}`;

            if (typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key] !== null) {
                // Recursively flatten nested objects
                flattenObject(obj[key], flatKey, result);
            } else {
                // Add primitive values or arrays directly
                result[flatKey] = obj[key];
            }
        }
    }
};

/**
 * Utility functions for handling HTTP requests and parameter serialization.
 */

/**
 * Generic function to flatten any object for GET request URL parameters.
 * Converts nested object structure to flat key-value pairs using dot notation.
 * This is needed because Spring Boot @ModelAttribute expects flattened URL parameters.
 *
 * @param obj - Any object to flatten
 * @returns Flattened object with dot notation for nested properties
 */
export const flattenObjectForUrlParams = (obj: any): Record<string, any> => {
    if (!obj || typeof obj !== 'object') {
        return {};
    }

    const flattened: Record<string, any> = {};
    flattenObjectRecursive(obj, '', flattened);
    return flattened;
};

/**
 * Recursively flattens an object using dot notation for nested properties.
 *
 * @param obj - Object to flatten
 * @param prefix - Prefix for the current level (empty string for root level)
 * @param result - Target object to store flattened properties
 */
const flattenObjectRecursive = (obj: any, prefix: string, result: Record<string, any>): void => {
    for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== undefined && obj[key] !== null) {
            const flatKey = prefix ? `${prefix}.${key}` : key;

            if (typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key] !== null && !(obj[key] instanceof Date)) {
                // Recursively flatten nested objects (but not arrays or dates)
                flattenObjectRecursive(obj[key], flatKey, result);
            } else {
                // Add primitive values, arrays, or dates directly
                result[flatKey] = obj[key];
            }
        }
    }
};

/**
 * Checks if an object is complex (has nested objects) and needs flattening.
 *
 * @param obj - Object to check
 * @returns true if the object has nested objects that need flattening
 */
export const isComplexObject = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return false;
    }

    for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== undefined && obj[key] !== null) {
            if (typeof obj[key] === 'object' && !Array.isArray(obj[key]) && !(obj[key] instanceof Date)) {
                return true;
            }
        }
    }
    return false;
}; 